import React, { useMemo } from 'react';
import { Box, ButtonBase, Divider, Paper, Tooltip, Typography } from '@mui/material';
import { STABILITY_EVENTS, stabilityDeviceSeries } from '../utils/diagnosticsHealth';

const CHART_HEIGHT = 44;
const BAR_GAP = 2;

const fmtClock = (ms) => new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const bucketCount = (startMs, endMs) => ((endMs - startMs) <= 2 * 3600 * 1000 ? 12 : 24);

const Bars = ({ values, color, startMs, bucketMs }) => {
  const max = Math.max(1, ...values);
  const width = 100 / values.length;
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', height: CHART_HEIGHT, gap: `${BAR_GAP}px` }}>
      {values.map((v, i) => {
        const from = startMs + i * bucketMs;
        return (
          <Tooltip
            key={i}
            arrow
            title={`${fmtClock(from)}–${fmtClock(from + bucketMs)} · ${v} device${v === 1 ? '' : 's'}`}
          >
            <Box
              sx={{
                width: `${width}%`,
                height: v ? `${Math.max(12, (v / max) * 100)}%` : 3,
                bgcolor: v ? color : 'action.hover',
                borderRadius: '3px 3px 1px 1px',
                opacity: v ? 0.85 : 1,
                '&:hover': { opacity: 1 },
              }}
            />
          </Tooltip>
        );
      })}
    </Box>
  );
};

// One panel for the backhaul drill-down: devices with at least one event of
// each type in the rows the current topic selection covers.
const DiagnosticsStabilityChart = ({ title, rows, start, end, colors, labels = {}, active, onSelect }) => {
  const startMs = new Date(start).getTime();
  const endMs = new Date(end).getTime();
  const valid = Number.isFinite(startMs) && Number.isFinite(endMs) && endMs > startMs;
  const buckets = valid ? bucketCount(startMs, endMs) : 1;
  const bucketMs = valid ? (endMs - startMs) / buckets : 0;
  const series = useMemo(
    () => stabilityDeviceSeries(rows, start, end, buckets),
    [rows, start, end, buckets],
  );
  const total = (rows || []).length;

  return (
    <Paper variant="outlined" sx={{ mb: 3 }}>
      <Box sx={{ px: 2, pt: 1.5, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="subtitle2">{title}</Typography>
        <Typography variant="caption" color="text.secondary">
          {`Devices with at least one event, of ${total}. Select any cards to filter the table.`}
        </Typography>
      </Box>
      <Divider />
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
        {STABILITY_EVENTS.map(({ type, label }, idx) => {
          const s = series[type];
          const isActive = Array.isArray(active) ? active.includes(type) : active === type;
          const color = colors[type];
          const pct = total ? Math.round((s.devices * 100) / total) : 0;
          return (
            <React.Fragment key={type}>
              {idx > 0 && <Divider flexItem orientation="vertical" sx={{ display: { xs: 'none', md: 'block' } }} />}
              <ButtonBase
                onClick={() => onSelect(type)}
                sx={{
                  flex: 1,
                  display: 'block',
                  textAlign: 'left',
                  px: 2,
                  py: 1.5,
                  boxShadow: isActive ? `inset 0 -3px 0 ${color}` : 'none',
                  bgcolor: isActive ? 'action.selected' : 'transparent',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color }} />
                  <Typography variant="body2" color="text.secondary">{labels[type] || label}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, my: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, lineHeight: 1 }}>{s.devices}</Typography>
                  <Typography variant="caption" color="text.secondary">{`${pct}%`}</Typography>
                  {Number.isFinite(s.current) && (
                    <Typography variant="caption" sx={{ ml: 'auto', color: s.current ? color : 'text.secondary', fontWeight: s.current ? 600 : 400 }}>
                      {`${s.current} offline now`}
                    </Typography>
                  )}
                </Box>
                {valid && <Bars values={s.buckets} color={color} startMs={startMs} bucketMs={bucketMs} />}
                {valid && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                    <Typography variant="caption" color="text.disabled">{fmtClock(startMs)}</Typography>
                    <Typography variant="caption" color="text.disabled">{fmtClock(endMs)}</Typography>
                  </Box>
                )}
              </ButtonBase>
            </React.Fragment>
          );
        })}
      </Box>
    </Paper>
  );
};

export default DiagnosticsStabilityChart;
